import { useEffect, useState } from 'react';

import { Button } from 'react-bootstrap';
import { useIntl } from 'react-intl';

import { useAppSelector } from 'hooks';
import { SectionTitle } from 'components/UI';
import { useAxios } from 'configs/requests';
import { Loading } from 'components/Fetch';
import { isPatientArchived } from 'store/patient/reducer';

import Goal from './Goal';

const MAX_GOALS = 20;

interface IGoalsProps {
  goals: ICarePlanGoal[];
  care_plan_id?: string;
  getCarePlan: any;
}

const Goals = ({ goals, care_plan_id, getCarePlan }: IGoalsProps) => {
  const intl = useIntl();
  const [_goals, setGoals] = useState<ICarePlanGoal[]>(goals);
  const patient = useAppSelector(({ patient }) => patient?.data);
  const _isPatientArchived = useAppSelector(isPatientArchived);

  const [{ loading }, postEditCarePlan] = useAxios(
    {
      url: `/clinician/Patient/${patient?.uid}/CarePlan`,
      method: 'POST'
    },
    { manual: true }
  );

  const addGoalEmpty = () => {
    if (!_goals.find((x) => x.edit_status === 'new')) {
      setGoals([
        ..._goals,
        {
          care_plan_id,
          goal: '',
          edit_status: 'new'
        }
      ]);
    }
  };

  const handleEditGoal = (goalEdited: any) => {
    if (!goalEdited) {
      /** clean empty goal Badge */
      setGoals(_goals.filter((goal) => goal.goal_id));
      return;
    }
    /** else handle new, deleted, updated, with basic edit_status */
    const newGoalWithNormalStatus = { ...goalEdited, edit_status: 'unchanged' };
    if (goalEdited.edit_status === 'new') {
      setGoals([..._goals.filter((goal) => goal.goal_id), newGoalWithNormalStatus]);
    } else if (goalEdited.edit_status === 'deleted') {
      setGoals(_goals.filter((goal) => goal.goal_id !== goalEdited.goal_id));
    } else if (goalEdited.edit_status === 'updated') {
      setGoals(
        _goals.map((goal) => {
          if (goal.goal_id === goalEdited.goal_id) {
            return newGoalWithNormalStatus;
          } else {
            return goal;
          }
        })
      );
    }
    getCarePlan();
  };

  useEffect(() => {
    setGoals(goals || []);
  }, [goals]);
  return (
    <div className="mb-5">
      <div className="d-flex justify-content-between align-items-center">
        <SectionTitle
          dataTestId="careplan-goals-title"
          label={intl.formatMessage({ id: 'goalsKey' })}
        />
        {loading && <Loading />}
        <Button
          data-test="careplan-goals-btn"
          size="sm"
          variant="link"
          onClick={addGoalEmpty}
          disabled={_goals.length >= MAX_GOALS || _isPatientArchived}
        >
          + {intl.formatMessage({ id: 'goalKey' })}
        </Button>
      </div>
      {_goals.length > 0 ? (
        _goals.map((goal, index) => (
          <Goal
            dataTestId={`careplan-goals-${index}`}
            key={goal.goal_id || Math.random()}
            goal={goal}
            handleEditGoal={handleEditGoal}
            postEditCarePlan={postEditCarePlan}
            disabled={_isPatientArchived}
          />
        ))
      ) : (
        <div
          className="bg-light text-center p-3 font-weight-bolder"
          data-test="careplan-goals-empty"
        >
          {intl.formatMessage({ id: 'noGoalsSet' })}
        </div>
      )}
    </div>
  );
};

export default Goals;
